import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowDown, Shield, ShieldCheck, ScanSearch, Lock, EyeOff, Ban,
  Fingerprint, CreditCard, MapPin, FolderLock, FileText, Building2, Puzzle,
  MonitorDown, Bot, Cpu, CheckCircle2,
} from "lucide-react";
import { Reveal, SectionHeading, Eyebrow } from "../components/common/Section";
import { Button } from "../components/ui/button";

function SkjoldOverlayMock() {
  const [mode, setMode] = useState("blocked");
  return (
    <div className="relative" data-testid="skjold-overlay-mock">
      {/* state toggle */}
      <div className="mb-3 inline-flex rounded-lg border border-white/10 bg-navy-800/70 p-1 text-xs">
        <button onClick={() => setMode("blocked")} data-testid="skjold-mock-blocked"
          className={`rounded-md px-3 py-1.5 font-medium transition-colors ${mode === "blocked" ? "bg-signal-red/15 text-signal-red" : "text-muted-foreground hover:text-foreground"}`}>Passport · blocked</button>
        <button onClick={() => setMode("redacted")} data-testid="skjold-mock-redacted"
          className={`rounded-md px-3 py-1.5 font-medium transition-colors ${mode === "redacted" ? "bg-signal-amber/15 text-signal-amber" : "text-muted-foreground hover:text-foreground"}`}>Bank statement · redacted</button>
      </div>

      <div className="rounded-lg border border-white/10 bg-navy-900/80 shadow-2xl shadow-black/40 overflow-hidden">
        <div className="flex items-center gap-2 border-b border-white/10 bg-navy-800/70 px-4 py-3">
          <span className="h-2.5 w-2.5 rounded-full bg-signal-red/70" />
          <span className="h-2.5 w-2.5 rounded-full bg-signal-amber/70" />
          <span className="h-2.5 w-2.5 rounded-full bg-signal-green/70" />
          <span className="ml-3 inline-flex items-center gap-1.5 text-xs text-muted-foreground"><Bot className="h-3.5 w-3.5 text-primary" /> AI assistant · New chat</span>
        </div>
        <div className="p-5 space-y-4">
          <div className="ml-auto max-w-[80%] rounded-lg rounded-br-sm bg-white/[0.04] border border-white/10 px-4 py-2.5 text-sm text-foreground">
            {mode === "blocked" ? "Can you summarise this document for me?" : "Can you pull the key figures from my statement?"}
          </div>

          {mode === "blocked" ? (
            <>
              <div className="ml-auto max-w-[88%]">
                <div className="rounded-xl border border-signal-red/40 bg-signal-red/[0.06] p-3">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-signal-red/15 text-signal-red"><Fingerprint className="h-5 w-5" /></span>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground">passport_scan.jpg</p>
                      <p className="text-xs text-muted-foreground">1.4 MB · image</p>
                    </div>
                    <span className="ml-auto inline-flex items-center gap-1 rounded-md border border-signal-red/40 bg-signal-red/10 px-2 py-1 text-[0.7rem] font-semibold uppercase tracking-wide text-signal-red"><Ban className="h-3.5 w-3.5" /> Restricted</span>
                  </div>
                </div>
              </div>
              <div className="rounded-xl border border-primary/30 bg-primary/[0.07] p-3.5">
                <div className="flex items-start gap-2.5">
                  <Shield className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <div>
                    <p className="text-sm font-semibold text-foreground">Blocked by Skjold</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">Passport detected — sharing identity documents is restricted by your AI policy. Nothing was uploaded or retained.</p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="ml-auto max-w-[92%]">
                <div className="rounded-xl border border-signal-amber/40 bg-signal-amber/[0.06] p-3">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-signal-amber/15 text-signal-amber"><CreditCard className="h-5 w-5" /></span>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground">bank_statement.pdf</p>
                      <p className="text-xs text-muted-foreground">312 KB · redacted before sending</p>
                    </div>
                    <span className="ml-auto inline-flex items-center gap-1 rounded-md border border-signal-amber/40 bg-signal-amber/10 px-2 py-1 text-[0.7rem] font-semibold uppercase tracking-wide text-signal-amber"><ScanSearch className="h-3.5 w-3.5" /> Redacted</span>
                  </div>
                  {/* redacted preview lines */}
                  <div className="mt-3 space-y-1.5 rounded-lg bg-navy-950/50 p-3 font-mono text-[0.7rem] text-muted-foreground">
                    <p>Account name: A. Customer</p>
                    <p className="flex items-center gap-2">Sort code: <span className="inline-block h-3 w-16 rounded-sm bg-foreground/70" /></p>
                    <p className="flex items-center gap-2">Account no: <span className="inline-block h-3 w-24 rounded-sm bg-foreground/70" /></p>
                    <p className="flex items-center gap-2">Balance: <span className="inline-block h-3 w-14 rounded-sm bg-foreground/70" /></p>
                  </div>
                </div>
              </div>
              <div className="rounded-xl border border-primary/30 bg-primary/[0.07] p-3.5">
                <div className="flex items-start gap-2.5">
                  <Shield className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <div>
                    <p className="text-sm font-semibold text-foreground">Redacted by Skjold</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">Sort code, account number and balance were removed on the device before sharing. The rest of the request continues — and nothing was retained.</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <div className="pointer-events-none absolute -inset-6 -z-10 rounded-[2rem] bg-primary/10 blur-3xl" aria-hidden="true" />
    </div>
  );
}

const FLOW = [
  { icon: FileText, step: "Compose or attach", desc: "A staff member types a prompt or attaches a document in ChatGPT, Claude or any web AI tool." },
  { icon: ScanSearch, step: "Local inspection", desc: "Skjold reads the message and file on the device, before anything is submitted." },
  { icon: Shield, step: "Policy check", desc: "Content is checked against your organisation's AI and data-handling policies." },
  { icon: Ban, step: "Allow, redact or block", desc: "Permitted content passes through; restricted material is redacted or blocked with a clear reason." },
  { icon: EyeOff, step: "Nothing retained", desc: "No document content is stored or transmitted by Skjold. The scan happens and is gone." },
];

const DETECTS = [
  { icon: Fingerprint, t: "Passports & ID documents", d: "Passports, driving licences and national identity documents." },
  { icon: CreditCard, t: "Bank statements & financial data", d: "Statements, card numbers, account details and financial records." },
  { icon: MapPin, t: "Proof of address", d: "Utility bills, tenancy documents and address confirmations." },
  { icon: FolderLock, t: "Client & case records", d: "Confidential client files, case notes and internal records." },
  { icon: Building2, t: "Restricted business material", d: "Pricing models, contracts and commercially sensitive documents." },
  { icon: ShieldCheck, t: "Anything in your policy", d: "Fully configurable — define your own categories, patterns and rules." },
];

const SURFACES = ["ChatGPT", "Claude", "Gemini", "Microsoft Copilot", "Perplexity", "Any web AI tool"];

const DELIVERY = [
  { icon: Puzzle, t: "Browser extension", d: "Installs into managed browsers and works across web-based AI tools your teams already use — no change to their workflow." },
  { icon: MonitorDown, t: "Desktop app / agent", d: "A lightweight local agent for desktop AI clients and file uploads, deployable through your existing endpoint management." },
];

const PRIVACY = [
  { icon: Cpu, t: "Runs locally", d: "Inspection happens on the device. Document content is never sent to Skjold servers." },
  { icon: EyeOff, t: "Nothing retained", d: "No copies, no logs of content. Only a minimal policy-decision reference can be kept if you choose." },
  { icon: Lock, t: "You stay in control", d: "Policies, categories and actions are set and versioned by your organisation." },
];

export default function Skjold() {
  useEffect(() => { document.title = "Skjold | norse3"; }, []);

  return (
    <div>
      {/* Custom two-column hero with overlay mock */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 grid-backdrop opacity-50" aria-hidden="true" />
        <div className="absolute inset-0 radial-fade" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 pt-32 pb-14 lg:pt-36 lg:pb-16">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-10 items-center">
            <div className="max-w-2xl animate-fade-up">
              <Eyebrow>Skjold · the shield</Eyebrow>
              <h1 className="mt-5 text-4xl sm:text-5xl font-bold leading-[1.06] text-foreground text-balance">
                Stop sensitive documents leaving before they're shared.
              </h1>
              <p className="mt-4 text-base font-semibold text-primary">In plain English: it stops staff pasting things like passports and bank statements into AI tools.</p>
              <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
                Skjold is a locally operated security overlay for ChatGPT, Claude and other
                generative AI platforms. It scans documents and messages on the device before
                anything is submitted, blocks restricted material and retains nothing — so staff
                can use AI without breaching your data-handling policy.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90">
                  <Link to="/book-a-demo" data-testid="skjold-hero-cta">Discuss a Skjold pilot <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
                </Button>
                <Button asChild variant="outline" className="border-white/15 bg-transparent hover:bg-white/5 text-foreground">
                  <Link to="/security-governance">Security &amp; governance</Link>
                </Button>
              </div>
            </div>
            <div className="animate-fade-up lg:pl-4" style={{ animationDelay: "120ms" }}>
              <SkjoldOverlayMock />
            </div>
          </div>
        </div>
      </section>

      {/* Promise band */}
      <section className="border-b border-white/10 py-8">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-4 sm:grid-cols-3">
            {[
              { icon: ScanSearch, t: "Scans locally", d: "Before anything is shared" },
              { icon: Ban, t: "Blocks restricted data", d: "Passports, bank statements, IDs, proof of address" },
              { icon: EyeOff, t: "Nothing retained", d: "No content stored or transmitted" },
            ].map((x) => {
              const Icon = x.icon;
              return (
                <div key={x.t} className="flex items-start gap-3 rounded-xl border border-white/10 bg-navy-900/60 p-5">
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/12 text-primary"><Icon className="h-5 w-5" /></span>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{x.t}</p>
                    <p className="text-sm text-muted-foreground">{x.d}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading
              eyebrow="How Skjold works"
              title="Inspect on the device. Decide before it's sent."
              subtitle="A local checkpoint between your staff and the AI tool — no content leaves until it has passed your policy."
            />
          </Reveal>
          <div className="mt-10 grid gap-4 lg:grid-cols-5 sm:grid-cols-2">
            {FLOW.map((f, i) => {
              const Icon = f.icon;
              return (
                <Reveal key={f.step} delay={i * 80}>
                  <div className="relative h-full rounded-xl border border-white/10 bg-navy-900/60 p-5">
                    <span className="absolute right-4 top-4 text-xs font-mono text-primary/50">0{i + 1}</span>
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary/12 text-primary"><Icon className="h-5 w-5" /></span>
                    <h3 className="mt-4 text-base font-semibold text-foreground">{f.step}</h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* What it detects */}
      <section className="border-t border-white/10 py-16 lg:py-20 bg-navy-950/40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading
              eyebrow="What Skjold can detect"
              title="Restricted material, caught before it's pasted or uploaded."
              subtitle="Skjold ships with common sensitive-data categories and can be configured to anything defined in your AI policy."
            />
          </Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {DETECTS.map((x, i) => {
              const Icon = x.icon;
              return (
                <Reveal key={x.t} delay={i * 60}>
                  <div className="h-full rounded-xl border border-white/10 bg-navy-900/60 p-6">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary/12 text-primary"><Icon className="h-5 w-5" /></span>
                    <h3 className="mt-4 text-base font-semibold text-foreground">{x.t}</h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{x.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <p className="mt-6 text-xs text-muted-foreground/70 max-w-2xl">Detection supports alignment with your policies; it does not replace your own controls or accountability. Coverage depends on document types, configuration and detection thresholds.</p>
        </div>
      </section>

      {/* Where it works */}
      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <SectionHeading
              eyebrow="Where Skjold works"
              title="Across the AI tools your teams already use."
              subtitle="Skjold sits in front of web-based generative AI, so protection follows the interaction rather than a single vendor."
            />
            <div className="mt-8 flex flex-wrap gap-2.5">
              {SURFACES.map((s) => (
                <span key={s} className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.03] px-4 py-2 text-sm text-foreground">
                  <Bot className="h-4 w-4 text-primary" /> {s}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="grid gap-4">
              {DELIVERY.map((x) => {
                const Icon = x.icon;
                return (
                  <div key={x.t} className="rounded-lg border border-white/10 bg-navy-900/60 p-6">
                    <div className="flex items-center gap-3">
                      <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary/12 text-primary"><Icon className="h-5 w-5" /></span>
                      <h3 className="text-lg font-semibold text-foreground">{x.t}</h3>
                    </div>
                    <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{x.d}</p>
                  </div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Privacy / no retention */}
      <section className="border-t border-white/10 py-16 lg:py-20 bg-navy-950/40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading
              eyebrow="Private by design"
              title="The scan happens on the device — and then it's gone."
              subtitle="Skjold is built to reduce data exposure, not add to it."
            />
          </Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-3">
            {PRIVACY.map((x, i) => {
              const Icon = x.icon;
              return (
                <Reveal key={x.t} delay={i * 80}>
                  <div className="h-full rounded-xl border border-white/10 bg-navy-900/60 p-6">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary/12 text-primary"><Icon className="h-5 w-5" /></span>
                    <h3 className="mt-4 text-base font-semibold text-foreground">{x.t}</h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{x.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Fits governance */}
      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="rounded-lg border border-white/10 bg-gradient-to-b from-navy-800/60 to-navy-900/60 p-8 lg:p-12">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <Reveal>
                <Eyebrow>Part of the norse3 layer</Eyebrow>
                <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-foreground text-balance">One policy, enforced wherever staff use AI.</h2>
                <p className="mt-4 text-muted-foreground leading-relaxed">
                  Skjold applies the same organisation-defined policy that governs your other AI
                  interactions. When you need an audit trail, permitted policy decisions can be
                  recorded as reviewable evidence — without keeping the sensitive content itself.
                </p>
                <div className="mt-7 flex flex-col sm:flex-row gap-3">
                  <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90">
                    <Link to="/book-a-demo">Discuss a Skjold pilot <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
                  </Button>
                  <Button asChild variant="outline" className="border-white/15 bg-transparent hover:bg-white/5 text-foreground">
                    <Link to="/governance-tools">Build a policy</Link>
                  </Button>
                </div>
              </Reveal>
              <Reveal delay={120}>
                <ul className="space-y-3">
                  {[
                    "Enforces your existing AI and data-handling policy",
                    "Blocks or redacts restricted material before submission",
                    "Keeps no document content — private by design",
                    "Optional reviewable evidence for compliance teams",
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-3 rounded-xl border border-white/10 bg-navy-900/60 p-4">
                      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-signal-green" />
                      <span className="text-sm text-foreground">{t}</span>
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
